import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useFlutterwave, closePaymentModal } from "flutterwave-react-v3";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import Header from "../components/Header";
import countriesLib from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import frLocale from "i18n-iso-countries/langs/fr.json";

countriesLib.registerLocale(enLocale);
countriesLib.registerLocale(frLocale);

const NODE_API = process.env.REACT_APP_NODE_MIDDLEWARE_API_URL;
const PHP_API  = process.env.REACT_APP_PHP_API_URL + "zuribuildAPI/";
const FLW_PUBLIC_KEY = process.env.REACT_APP_FLUTTERWAVE_API_KEY;

const DOMAIN_PRICE_USD = 200;

// Country code → currency mapping
const COUNTRY_CURRENCY = {
  CM: "XAF", CI: "XOF", SN: "XOF", NG: "NGN", GH: "GHS", KE: "KES",
  ZA: "ZAR", ML: "XOF", BF: "XOF", GN: "GNF", TG: "XOF", BJ: "XOF",
  CG: "XAF", CD: "CDF", GA: "XAF", RW: "RWF", ET: "ETB", TZ: "TZS",
  UG: "UGX", MA: "MAD", DZ: "DZD", TN: "TND", EG: "EGP", CA: "CAD",
  US: "USD", FR: "EUR", GB: "GBP", BE: "EUR", CH: "CHF", DE: "EUR",
  PT: "EUR", BR: "BRL", CN: "CNY", IN: "INR", JP: "JPY", AU: "AUD",
  MX: "MXN", AR: "ARS", NG: "NGN", RU: "RUB", KR: "KRW", TR: "TRY",
  AE: "AED", SA: "SAR", ZM: "ZMW", MW: "MWK", MZ: "MZN", AO: "AOA",
  NE: "XOF", TD: "XAF", CF: "XAF", GQ: "XAF", ST: "STN", CV: "CVE",
  GM: "GMD", SL: "SLE", LR: "LRD", GW: "XOF", MR: "MRU", SO: "SOS",
  DJ: "DJF", ER: "ERN", SS: "SSP", SD: "SDG", LY: "LYD", MG: "MGA",
  MU: "MUR", SC: "SCR", KM: "KMF", BI: "BIF", NL: "EUR", IT: "EUR",
  ES: "EUR", PL: "PLN", SE: "SEK", NO: "NOK", DK: "DKK", FI: "EUR",
  AT: "EUR", IE: "EUR", NZ: "NZD", SG: "SGD", HK: "HKD", TH: "THB",
  MY: "MYR", ID: "IDR", PH: "PHP", VN: "VND", PK: "PKR", BD: "BDT",
};

export default function DomainRegistration() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const firebaseUser = JSON.parse(localStorage.getItem('firebase_user') || '{}');
  const memberId = firebaseUser?.uid || '';

  const [domainInput, setDomainInput]         = useState('');
  const [searching, setSearching]             = useState(false);
  const [result, setResult]                   = useState(null);
  const [searched, setSearched]               = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [exchangeRates, setExchangeRates]     = useState({});
  const [ratesLoaded, setRatesLoaded]         = useState(false);
  const [websites, setWebsites]               = useState([]);
  const [selectedFolder, setSelectedFolder]   = useState('');
  const [paying, setPaying]                   = useState(false);

  // Rebuild country list when language changes
  const countryList = useMemo(() => {
    const lang = i18n.language === 'fr' ? 'fr' : 'en';
    return Object.entries(countriesLib.getNames(lang, { select: 'official' }))
      .map(([code, name]) => ({
        code,
        name,
        currency: COUNTRY_CURRENCY[code] || 'USD',
      }))
      .sort((a, b) => a.name.localeCompare(b.name, lang));
  }, [i18n.language]);

  useEffect(() => {
    if (!localStorage.getItem('user_info') || !memberId) { navigate('/login'); return; }
    loadWebsites();
    loadExchangeRates();
  }, []);

  const loadWebsites = () => {
    axios.post(PHP_API + "get-websites.php", { member_id: memberId })
      .then(res => { if (Array.isArray(res.data)) setWebsites(res.data); })
      .catch(() => {});
  };

  const loadExchangeRates = async () => {
    try {
      const res = await axios.get('https://open.er-api.com/v6/latest/USD');
      if (res.data?.rates) { setExchangeRates(res.data.rates); setRatesLoaded(true); }
    } catch { toast.error(t("domain-registration.error_rates")); }
  };

  const getLocalPrice = (priceUSD) => {
    if (!selectedCountry || !ratesLoaded) return null;
    const rate = exchangeRates[selectedCountry.currency];
    if (!rate) return null;
    return (priceUSD * rate).toFixed(2);
  };

  const handleSearch = async () => {
    const name = domainInput.trim().toLowerCase();
    if (!name) { toast.error(t("domain-registration.error_no_domain")); return; }
    if (!name.includes('.')) { toast.error(t("domain-registration.error_no_extension")); return; }
    setSearching(true); setResult(null); setSearched(false);
    try {
      const res = await axios.post(`${NODE_API}/api/check-domain`, { domain: name });
      if (res.data.success) { setResult(res.data); setSearched(true); }
      else toast.error(res.data.message);
    } catch { toast.error(t("domain-registration.error_search")); }
    finally { setSearching(false); }
  };

  const localPrice = selectedCountry && ratesLoaded ? getLocalPrice(DOMAIN_PRICE_USD) : null;

  const flwConfig = {
    public_key: FLW_PUBLIC_KEY,
    tx_ref: `zuribuild-domain-${Date.now()}`,
    amount: parseFloat(localPrice) || DOMAIN_PRICE_USD,
    currency: selectedCountry?.currency || 'USD',
    payment_options: "card,mobilemoney,ussd",
    customer: {
      email: firebaseUser?.email || '',
      name: firebaseUser?.displayName || '',
    },
    meta: {
      type: "domain_purchase",
      user_id: memberId,
      user_email: firebaseUser?.email || '',
      user_name: firebaseUser?.displayName || '',
      domain_name: result?.domain?.split('.')?.[0] || '',
      tld: result?.tld || '',
      amount_usd: DOMAIN_PRICE_USD,
      amount_local: localPrice,
      currency: selectedCountry?.currency || 'USD',
      country: selectedCountry?.name || '',
      folder_name: selectedFolder,
      app_language: i18n.language,
    },
    customizations: {
      title: "ZuriBuild — Domain Purchase",
      description: result?.domain ? `${result.domain} — 1 year` : "Domain Purchase",
      logo: "https://zuribuild.com/zuribuild.png",
    },
  };

  const handleFlutterPayment = useFlutterwave(flwConfig);

  const handlePay = () => {
    if (!result?.available) { toast.error(t("domain-registration.error_not_available")); return; }
    if (!selectedCountry)   { toast.error(t("domain-registration.error_no_country")); return; }
    if (!selectedFolder)    { toast.error(t("domain-registration.error_no_website")); return; }
    if (!ratesLoaded)       { toast.error(t("domain-registration.error_rates_loading")); return; }
    if (!localPrice)        { toast.error(t("domain-registration.error_currency_unsupported")); return; }
    setPaying(true);
    handleFlutterPayment({
      callback: (response) => {
        closePaymentModal();
        setPaying(false);
        if (response.status === 'successful') {
          toast.success(t("domain-registration.payment_success"));
		  navigate('/success-domain');
        } else {
          toast.error(t("domain-registration.payment_failed"));
        }
      },
      onClose: () => { setPaying(false); },
    });
  };

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'Inter', -apple-system, sans-serif; background: #f8f8f8; }
        .domain-container { max-width: 760px; margin: 0 auto; padding: 32px 16px 60px; }
        .search-bar { display: flex; gap: 10px; margin-bottom: 8px; }
        .search-input { flex: 1; height: 53px; border-radius: 26px; border: 2px solid #ddd; padding: 20px; font-size: 16px; outline: none; transition: border 0.2s; }
        .search-input:focus { border-color: #7C3AED; }
        .search-btn { height: 52px; padding: 0 28px; background: #7C3AED; color: white; border: none; border-radius: 26px; font-size: 15px; font-weight: 600; cursor: pointer; white-space: nowrap; }
        .search-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .domain-hint { font-size: 12px; color: #aaa; margin-bottom: 24px; padding-left: 8px; }
        .config-section { background: white; border-radius: 14px; padding: 20px; border: 1px solid #eee; margin-bottom: 16px; }
        .config-title { font-size: 14px; font-weight: 600; color: #333; margin-bottom: 14px; }
        .country-select, .site-select { width: 100%; padding: 10px 16px; border: 1.5px solid #ddd; border-radius: 10px; font-size: 15px; outline: none; }
        .country-select:focus, .site-select:focus { border-color: #7C3AED; }
        .price-summary { background: linear-gradient(135deg, #7C3AED, #a855f7); border-radius: 14px; padding: 20px; color: white; margin-bottom: 20px; }
        .price-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; font-size: 14px; }
        .price-row:last-child { margin-bottom: 0; font-size: 18px; font-weight: 700; }
        .pay-btn { width: 100%; height: 54px; background: #7C3AED; color: white; border: none; border-radius: 14px; font-size: 16px; font-weight: 700; cursor: pointer; transition: 0.2s; }
        .pay-btn:hover { background: #5a0e8f; }
        .pay-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .page-title { font-size: 24px; font-weight: 800; color: #111; margin-bottom: 6px; letter-spacing: -0.5px; }
        .page-subtitle { font-size: 14px; color: #888; margin-bottom: 28px; }
        @media (max-width: 600px) { .search-bar { flex-direction: column; } }
      `}</style>

      <Header />

      <div className="domain-container">
	  <div  style={{
		  marginTop:'85px'
	  }}>
        <h1 className="page-title">{t("domain-registration.register_new_domain")}</h1>
        <p className="page-subtitle">{t("domain-registration.subtitle")}</p>
     </div>
        {/* Search Bar */}
        <div className="search-bar">
          <input
            className="search-input"
            type="text"
			
			style={{
				//height:'100px'
			}}
			
			
            placeholder={t("domain-registration.placeholder_domain")}
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button className="search-btn" onClick={handleSearch} disabled={searching}>
            {searching
              ? <><span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} />{t("domain-registration.searching")}</>
              : t("domain-registration.lookup_for_domain")
            }
          </button>
        </div>
        <p className="domain-hint">{t("domain-registration.domain_hint")}</p>

        {/* Result */}
        {searched && result && (
          <div style={{
            background: result.available ? '#f0fdf4' : '#fff0f0',
            border: `2px solid ${result.available ? '#16a34a' : '#e11d48'}`,
            borderRadius: 14, padding: 20, marginBottom: 24
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#111' }}>{result.domain}</div>
                <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>
                  ${DOMAIN_PRICE_USD} USD {t("domain-registration.price_per_year")}
                </div>
              </div>
              <span style={{
                fontSize: 14, fontWeight: 700, padding: '6px 16px', borderRadius: 20,
                background: result.available ? '#16a34a' : '#e11d48', color: 'white'
              }}>
                {result.available ? t("domain-registration.available") : t("domain-registration.not_available")}
              </span>
            </div>
          </div>
        )}

        {/* Country + Website — only if available */}
        {searched && result?.available && (
          <>
            <div className="config-section">
              <p className="config-title">🌍 {t("domain-registration.select_country")}</p>
              <select
                className="country-select"
                value={selectedCountry?.code || ''}
                onChange={(e) => {
                  const c = countryList.find(c => c.code === e.target.value);
                  setSelectedCountry(c || null);
                }}
              >
                <option value="">{t("domain-registration.select_country_placeholder")}</option>
                {countryList.map(c => (
                  <option key={c.code} value={c.code}>
                    {c.name} ({c.currency})
                  </option>
                ))}
              </select>
            </div>

            <div className="config-section">
              <p className="config-title">🏢 {t("domain-registration.select_website")}</p>
              <select
                className="site-select"
                value={selectedFolder}
                onChange={(e) => setSelectedFolder(e.target.value)}
              >
                <option value="">{t("domain-registration.select_website_placeholder")}</option>
                {websites.map(w => (
                  <option key={w.folder_name} value={w.folder_name}>
                    {w.website_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Price Summary */}
            {selectedCountry && ratesLoaded && localPrice && (
              <div className="price-summary">
                <div className="price-row"><span>{t("domain-registration.domain_label")}</span><span>{result.domain}</span></div>
                <div className="price-row"><span>{t("domain-registration.duration_label")}</span><span>{t("domain-registration.duration_value")}</span></div>
                <div className="price-row"><span>{t("domain-registration.amount_usd")}</span><span>${DOMAIN_PRICE_USD}</span></div>
                <div className="price-row">
                  <span>{t("domain-registration.your_currency")} ({selectedCountry.currency})</span>
                  <span>{localPrice} {selectedCountry.currency}</span>
                </div>
              </div>
            )}

            <button className="pay-btn" onClick={handlePay} disabled={paying || !selectedCountry || !selectedFolder}>
              {paying
                ? <><span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} />{t("domain-registration.processing")}</>
                : `💳 ${t("domain-registration.pay_btn").replace('{{amount}}', localPrice || '$' + DOMAIN_PRICE_USD).replace('{{currency}}', selectedCountry?.currency || 'USD')}`
              }
            </button>
          </>
        )}
      </div>

      <ToastContainer position="bottom-right" autoClose={4000} />
    </>
  );
}